import React, { useState } from 'react';

// Interest Modal Component
function InterestModal({ isOpen, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    institution: '',
    role: '',
    otherRole: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const roleOptions = [
    { value: 'teacher', label: 'Teacher' },
    { value: 'special_educator', label: 'Special Educator' },
    { value: 'reading_specialist', label: 'Reading Specialist' },
    { value: 'math_specialist', label: 'Math Specialist' },
    { value: 'behavior_specialist', label: 'Behavior Specialist' },
    { value: 'administrator', label: 'Administrator' },
    { value: 'other', label: 'Other' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.institution.trim()) {
      newErrors.institution = 'Institution name is required';
    }
    
    if (!formData.role) {
      newErrors.role = 'Please select a role';
    }
    
    if (formData.role === 'other' && !formData.otherRole.trim()) {
      newErrors.otherRole = 'Please specify your role';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare data for submission
      const submissionData = {
        ...formData,
        role: formData.role === 'other' ? formData.otherRole : formData.role,
        timestamp: new Date().toISOString()
      };
      
      await onSubmit(submissionData);
      
      // Reset form on success
      setFormData({
        name: '',
        email: '',
        institution: '',
        role: '',
        otherRole: ''
      });
      
    } catch (error) {
      console.error('Submission error:', error);
      // Handle error (you might want to show an error message)
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && !isSubmitting) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, isSubmitting]);

  if (!isOpen) return null;

  return (
    <div className="interest-modal-overlay" onClick={handleBackdropClick}>
      <div className="interest-modal-content">
        <div className="interest-modal-header">
          <h3 className="interest-modal-title">Express Interest in InDe Platform</h3>
          <button 
            className="interest-modal-close" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            &times;
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="interest-form">
          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={errors.name ? 'error' : ''}
              disabled={isSubmitting}
              placeholder="Enter your full name"
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={errors.email ? 'error' : ''}
              disabled={isSubmitting}
              placeholder="Enter your email address"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="institution">Institution Name *</label>
            <input
              type="text"
              id="institution"
              name="institution"
              value={formData.institution}
              onChange={handleInputChange}
              className={errors.institution ? 'error' : ''}
              disabled={isSubmitting}
              placeholder="School/District name (or 'Home School' if homeschooling)"
            />
            <small className="form-note">
              If you homeschool your child, simply enter "Home School"
            </small>
            {errors.institution && <span className="error-message">{errors.institution}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="role">Role *</label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              className={errors.role ? 'error' : ''}
              disabled={isSubmitting}
            >
              <option value="">Select your role</option>
              {roleOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.role && <span className="error-message">{errors.role}</span>}
          </div>

          {formData.role === 'other' && (
            <div className="form-group">
              <label htmlFor="otherRole">Please specify your role *</label>
              <textarea
                id="otherRole"
                name="otherRole"
                value={formData.otherRole}
                onChange={handleInputChange}
                className={errors.otherRole ? 'error' : ''}
                disabled={isSubmitting}
                placeholder="Please describe your role"
                rows="3"
              />
              {errors.otherRole && <span className="error-message">{errors.otherRole}</span>}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Express Interest'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default InterestModal;
